<template>
    <div class="content lic-mng-page">
        <div class="container">
            <div class="page-title">
                <h2 class="tit">라이선스 관리</h2>
            </div>

            <div class="list-controls">
                <div class="tab swiper-container">
                    <ul class="swiper-wrapper">
                        <li class="swiper-slide" :class="{ 'active': state.activeMainTab === 'home' }">
                            <a href="javascript:;" @click="switchMainTab('home')">홈</a>
                        </li>
                        <!-- 팀 관리자와 보조 관리자만 볼 수 있는 탭들 -->
                        <template v-if="hasAdminPermission">
                            <li class="swiper-slide" :class="{ 'active': state.activeMainTab === 'user' }">
                                <a href="javascript:;" @click="switchMainTab('user')">사용자 관리</a>
                            </li>
                            <li class="swiper-slide" :class="{ 'active': state.activeMainTab === 'contract' }">
                                <a href="javascript:;" @click="switchMainTab('contract')">계약</a>
                            </li>
                            <li class="swiper-slide" :class="{ 'active': state.activeMainTab === 'analytics' }">
                                <a href="javascript:;" @click="switchMainTab('analytics')">사용분석</a>
                            </li>
                            <li class="swiper-slide" :class="{ 'active': state.activeMainTab === 'setting' }">
                                <a href="javascript:;" @click="switchMainTab('setting')">설정</a>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>

            <div class="panel">
                <component :is="activeComponent"></component>
            </div>
        </div>
    </div>
    
</template>

<script>
export default {
    name: 'licenseManagement',
    components: {
    },
    data() {
        // 초기값 설정 시 쿼리 파라미터 확인하여 홈 탭이 스쳐 지나가는 문제 방지
        const queryMainTab = this.$route?.query?.mainTab;
        const initialTab = (queryMainTab && ['home', 'user', 'contract', 'analytics', 'setting'].includes(queryMainTab))
            ? queryMainTab
            : 'home';
        
        return {
            state: {
                activeMainTab: initialTab,
                userRole: null, // 'CREW_ADMIN', 'SUB_ADMIN', 'MEMBER'
                loadingRole: true,
            },
        };
    },
    computed: {
        activeComponent() {
            if (this.state.activeMainTab === 'home') return () => import('./licenseHome.vue');
            if (this.state.activeMainTab === 'user') return () => import('./userLicenseManagement.vue');
            if (this.state.activeMainTab === 'contract') return () => import('./licenseContract.vue');
            if (this.state.activeMainTab === 'analytics') return () => import('./licenseAnalytics.vue');
            if (this.state.activeMainTab === 'setting') return () => import('./setting.vue');
            return () => import('./licenseHome.vue');
        },
        hasAdminPermission() {
            // 팀 관리자 또는 보조 관리자인 경우 true
            return this.state.userRole === 'CREW_ADMIN' || this.state.userRole === 'SUB_ADMIN';
        },
    },
    async created() {
        if (!this.isLogin()) {
            await this.requireLogin(this, false);
        }
        await this.loadUserRole();
    },
    methods: {
        async loadUserRole() {
            if (!this.isLogin()) {
                return;
            }
            
            this.state.loadingRole = true;
            try {
                const account = this.getSessionProc();
                if (!account) {
                    this.state.userRole = 'MEMBER';
                    return;
                }

                // 로그인 시점 crewRole은 오래될 수 있음 — 서버에서 최고 팀 권한 재조회 후 Vue 세션 동기화
                await new Promise((resolve) => {
                    this.axiosCaller.get(
                        this,
                        this.APIs.SESSION + '/refreshCrewRole',
                        {},
                        (res) => {
                            try {
                                const data = res?.data || {};
                                if (data.status === 'ok' && data.crewRole) {
                                    const acc = this.getSessionProc();
                                    if (acc) {
                                        acc.crewRole = data.crewRole;
                                        this.$session.set(this.SESSION_KEY(), acc);
                                    }
                                    this.state.userRole = data.crewRole;
                                } else {
                                    this.state.userRole = account.crewRole || 'MEMBER';
                                }
                            } finally {
                                resolve();
                            }
                        },
                        () => {
                            this.state.userRole = account.crewRole || 'MEMBER';
                            resolve();
                        }
                    );
                });

                if (this.state.userRole === 'MEMBER' && this.state.activeMainTab !== 'home') {
                    this.$router.replace({
                        name: 'licenseManagement',
                        query: { mainTab: 'home' }
                    });
                    this.state.activeMainTab = 'home';
                } else {
                    this.syncActiveTabFromQuery();
                }
            } catch (e) {
                console.error('Error loading user role:', e);
                this.state.userRole = 'MEMBER';
            } finally {
                this.state.loadingRole = false;
            }
        },
        syncActiveTabFromQuery() {
            const queryMainTab = this.$route.query?.mainTab;
            if (!queryMainTab || !['home', 'user', 'contract', 'analytics', 'setting'].includes(queryMainTab)) {
                return;
            }
            if (this.state.userRole === 'MEMBER' && queryMainTab !== 'home') {
                this.state.activeMainTab = 'home';
                return;
            }
            this.state.activeMainTab = queryMainTab;
        },
        switchMainTab(tab) {
            // 일반 팀원은 홈 탭만 접근 가능
            if (this.state.userRole === 'MEMBER' && tab !== 'home') {
                this.gritterErr('권한이 없습니다. 홈 화면만 접근 가능합니다.');
                return;
            }
            
            if (this.state.activeMainTab === tab) return;
            
            // 현재 스크롤 위치 저장
            const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
            
            // 탭 변경 시 쿼리 파라미터 업데이트 (기존 쿼리 파라미터 유지)
            const newQuery = {
                ...this.$route.query,
                mainTab: tab
            };
            
            // 'user' 탭을 클릭했을 때는 항상 팀원별(기본값)로 설정
            if (tab === 'user') {
                newQuery.subTab = 'members';
            }
            
            this.$router.replace({
                name: 'licenseManagement',
                query: newQuery
            }).then(() => {
                // 스크롤 위치 복원
                this.$nextTick(() => {
                    window.scrollTo(0, scrollPosition);
                });
            });
        },
        gritterErr(msg) {
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.';
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('error', m, 'error', 2000)
                : console.error('[ERR]', m);
        },
    },
    watch: {
        '$route.query'(newQuery, oldQuery) {
            const mainTab = newQuery?.mainTab;
            if (mainTab && ['home', 'user', 'contract', 'analytics', 'setting'].includes(mainTab)) {
                // 일반 팀원이 관리자 전용 탭에 접근하려고 하면 홈으로 리다이렉트
                if (this.state.userRole === 'MEMBER' && mainTab !== 'home') {
                    this.$router.replace({
                        name: 'licenseManagement',
                        query: { mainTab: 'home' }
                    });
                    this.state.activeMainTab = 'home';
                    this.gritterErr('권한이 없습니다. 홈 화면만 접근 가능합니다.');
                    return;
                }
                
                // mainTab이 실제로 변경된 경우에만 처리
                if (oldQuery?.mainTab !== mainTab) {
                    this.state.activeMainTab = mainTab;
                }
            }
        },
    },
    mounted() {
        if (!document.querySelector('link[href="/ssflex/assets/css/crew-license-manager.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/ssflex/assets/css/crew-license-manager.css';
            document.head.appendChild(link);
        }
    },
};
</script>

<style scoped>
/* 외부 CSS 파일 사용: /ssflex/assets/css/crew-license-manager.css */
/* 서브 탭 컴포넌트는 해당 CSS를 공유합니다 */
</style>
